import React from 'react';
import { Box, Stepper, Step, StepLabel, LinearProgress, styled, useMediaQuery, Typography, useTheme } from '@mui/material';
interface ProgressIndicatorProps {
  currentStep: number;
  totalSteps: number;
  stepTitles: string[];
}

// Custom styled components for the stepper
const StyledStepper = styled(Stepper)(({ theme }) => ({
  padding: theme.spacing(2, 0),
  '& .MuiStepIcon-root': {
    color: '#757575', // Even darker gray (Material-UI's gray-600) for better contrast with white text
    [theme.breakpoints.down('sm')]: {
      // Make icons slightly smaller on mobile for better spacing
      fontSize: '1.2rem',
    },
  },
  '& .MuiStepIcon-root.Mui-active': {
    color: '#ff6a00', // Use the exact orange color
  },
  '& .MuiStepIcon-root.Mui-completed': {
    color: '#ff6a00', // Use the exact orange color
  },
  '& .MuiStepIcon-text': {
    fill: '#FFFFFF', // White text inside the circle
  },
  '& .MuiStepLabel-label': {
    color: '#708181', // Default label color
    [theme.breakpoints.down('sm')]: {
      // Hide labels on mobile to prevent overcrowding
      display: 'none',
    },
  },
  '& .MuiStepLabel-label.Mui-active': {
    color: '#ff6a00', // Active label color
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      // Hide all labels on mobile since we show the step name above the progress bar
      display: 'none',
    },
  },
  '& .MuiStepLabel-label.Mui-completed': {
    color: '#708181', // Completed label color
  }
}));

const ProgressBar = styled(LinearProgress)(({ theme }) => ({
  height: 6,
  borderRadius: 3,
  marginBottom: theme.spacing(2),
  backgroundColor: '#e0e0e0',
  '& .MuiLinearProgress-bar': {
    backgroundColor: '#ff6a00', // Use the exact orange color
  }
}));

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({ 
  currentStep, 
  totalSteps, 
  stepTitles 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // Calculate progress percentage
  const progressPercentage = Math.round((currentStep / (totalSteps - 1)) * 100);

  return (
    <Box sx={{ width: '100%', mb: { xs: 2, sm: 4 } }}>
      {/* Display current step label above progress bar on mobile */}
      {isMobile && (
        <Typography 
          variant="body2" 
          color="primary.main" 
          align="center" 
          fontWeight="500" 
          sx={{ mb: 1 }}
        >
          Step {currentStep + 1}: {stepTitles[currentStep]}
        </Typography>
      )}
      
      <ProgressBar 
        variant="determinate" 
        value={progressPercentage} 
      />
      
      {/* On very small screens, use a more compact stepper */}
      {/* Hide stepper on mobile devices - show only on tablet and up */}
      {!isMobile && (
        <StyledStepper 
          activeStep={currentStep} 
          alternativeLabel
        >
          {stepTitles.map((label, index) => (
            <Step key={index} completed={currentStep > index}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </StyledStepper>
      )}
    </Box>
  );
};

export default ProgressIndicator;